import { context, Span, trace } from '@opentelemetry/api'
import { useRef } from 'react'

import { useStore } from '~/store/store'
import { INSTRUMENTATION_NAME } from '~/utils/observability'

import { clearGlobalActiveContextIfOwner, setGlobalActiveContext } from './globalActiveContext'
import { useLoadingObserver } from './useLoadingObserver'
import { useOtelLoadingMetric } from './useOtelLoadingMetric'

const tracer = trace.getTracer(INSTRUMENTATION_NAME)

export type Props = {
    name: string
    isLoading: boolean
    queryKey?: string
}

/**
 * Instruments Otel metrics and spans to capture user-perceived loading latency when fetching entirely new data that triggers
 * `isLoading` and blocks the UI, rather than background refetches.
 *
 * IMPORTANT: do NOT use this hook for components that mount simultaneously as this will produce overlapping and disrupted spans.
 * If need to track loading for such components, use `useOtelLoadingMetric` instead.
 *
 * By initiating the span synchronously during render, this hook ensures that the span enters the active global context before any
 * asynchronous query functions execute. This allows the fetch calls to be correctly associated with the component's render span.
 *
 * Due to the singleton nature of the active global context, this hook assumes a single active span at any given time. While ideal
 * for page-level components that blocks the rest of the UI during loading, this may produce overlapping and disrupted spans if
 * used in nested components that initiate spans before the parent span has ended.
 *
 * Also, any fetch calls initiated while the span is active will be linked to it, even if triggered by other components. This might
 * result in unrelated fetch calls being included in the span, but better yet it captures the full scope of background activity.
 *
 * @param name - A name, typically in the format of `Page.ComponentName` (e.g. OP.WeekView.Calendar), to provide context for the metrics and spans.
 * @param isLoading - A boolean indicating whether the component is currently loading data.
 * @param queryKey - A unique key used to track the query args.
 */
export const useOtelLoadingSpanAndMetric = ({ name, isLoading, queryKey }: Props) => {
    const spanKey = queryKey ?? 'static'
    const spanRef = useRef<Span | null>(null)

    // Must be called before the span tracking to link the metric to the span via the global active context
    useOtelLoadingMetric({ name, isLoading, queryKey })

    useLoadingObserver(isLoading, spanKey, {
        onStart: () => {
            const { display_name, username, issuer } = useStore.getState().user
            const isDeepinsightUser = issuer === 'google' || issuer === 'mock'

            spanRef.current = tracer.startSpan(`${name} Loading`, {
                attributes: {
                    'di.component.path': name,
                    'di.component.span_key': spanKey,
                    'di.user.display_name': display_name ?? 'Unknown',
                    'di.user.username': username ?? 'Unknown',
                    'di.user.is_deepinsight': isDeepinsightUser,
                },
            })

            // Create a new context with the span set to active (using the currently active context as parent)
            const updatedContext = trace.setSpan(context.active(), spanRef.current)
            // Update the global active context so that it can be picked up by `base-client` for correct span parenting
            setGlobalActiveContext({ ctx: updatedContext, owner: spanRef.current })
        },
        onEnd: endCause => {
            if (!spanRef.current) return

            // Only clear the global active context if a new span hasn't overwritten it
            // (e.g. if the user navigates to a new date range or sub-page while loading that mounts a new component with its own span)
            clearGlobalActiveContextIfOwner(spanRef.current)

            spanRef.current.setAttribute('di.span.endCause', endCause)
            spanRef.current.end()
            spanRef.current = null
        },
    })
}
